<template>
  <div :class="['formRange', { 'formRange--withPadding': separateLabels }]">
    <div v-if="!separateLabels" class="formRange__title">
      {{ label }}
    </div>
    <div class="formRange__inner">
      <div :class="['formRange__inputs', { 'formRange__inputs--smallerDash': separateLabels }]">
        <div
          :class="['formRange__inputWrapper', { 'formRange__inputWrapper--wider': separateLabels }]"
        >
          <label class="formRange__label" :for="`${name}-from`">
            <input
              id="`${name}-from`"
              v-model="rangeValue.from"
              type="number"
              :placeholder="min"
              :class="[
                'formRange__input',
                {
                  'formRange__input--tall': isTall,
                },
              ]"
              @change="updateSlider"
            />
            <span v-if="postfix" class="formRange__postfix">
              {{ postfix }}
            </span>
            <span
              v-if="separateLabels"
              :class="['formRange__labelText', 'formRange__labelText--top']"
            >
              {{ `${label} od` }}
            </span>
          </label>
        </div>
        <div
          :class="['formRange__inputWrapper', { 'formRange__inputWrapper--wider': separateLabels }]"
        >
          <label class="formRange__label" :for="`${name}-to`">
            <input
              v-model="rangeValue.to"
              type="number"
              :placeholder="max"
              :class="[
                'formRange__input',
                {
                  'formRange__input--tall': isTall,
                },
              ]"
              @change="updateSlider"
            />
            <span v-if="postfix" class="formRange__postfix">
              {{ postfix }}
            </span>
            <span
              v-if="separateLabels"
              :class="['formRange__labelText', 'formRange__labelText--top']"
            >
              {{ `${label} do` }}
            </span>
          </label>
        </div>
      </div>
      <div class="formRange__range">
        <div id="slider" ref="slider"></div>
      </div>
    </div>
  </div>
</template>

<script>
import noUiSlider from 'nouislider';
export default {
  name: 'FormRange',
  props: {
    id: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: null,
    },
    value: {
      type: [Object, Array, String],
      default: () => {},
    },
    min: {
      type: Number,
      default: undefined,
    },
    max: {
      type: Number,
      default: undefined,
    },
    postfix: {
      type: String,
      default: '',
    },
    isTall: {
      type: Boolean,
      default: false,
    },
    separateLabels: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      slider: null,
      isFirstLoad: true,
      sliderOptions: {
        step: 1,
      },
    };
  },
  computed: {
    rangeValue() {
      return {
        from: this.value ? this.value.from : this.min,
        to: this.value ? this.value.to : this.max,
      };
    },
  },
  watch: {
    min() {
      this.updateOptions();
    },
    max() {
      this.updateOptions();
    },
    rangeValue: {
      handler(value) {
        this.slider.updateOptions({
          start: [value.from, value.to],
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.createSlider();

    this.slider.on('change', (values, handle) => {
      if (handle === 1) {
        this.rangeValue.to = parseFloat(values[handle]);
      } else {
        this.rangeValue.from = parseFloat(values[handle]);
      }

      this.$emit('input', this.rangeValue);
      this.$emit('emitObject', { name: this.name, value: this.rangeValue });
    });
  },
  methods: {
    createSlider() {
      this.slider = noUiSlider.create(this.$refs.slider, {
        start: [this.rangeValue.from, this.rangeValue.to],
        step: this.sliderOptions.step,
        connect: true,
        range: {
          min: Math.floor(this.min),
          max: Math.ceil(this.max),
        },
      });
    },
    updateSlider() {
      this.slider.set([this.rangeValue.from, this.rangeValue.to]);
      this.$emit('input', this.rangeValue);
      this.$emit('emitObject', { name: this.name, value: this.rangeValue });
    },
    updateOptions() {
      this.slider.updateOptions({
        start: [this.rangeValue.from, this.rangeValue.to],
        range: {
          min: Math.floor(this.min),
          max: Math.ceil(this.max),
        },
      });
    },
  },
};
</script>

<style lang="scss">
.formRange {
  $root: &;

  &--withPadding {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  &__title {
    margin-bottom: 1.2rem;
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 600;
    color: $cBlack;
  }

  &__inputs {
    position: relative;
    margin-bottom: 3rem;
    display: flex;
    justify-content: space-between;

    &:before {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateX(-50%);
      width: 2.5rem;
      height: 1px;
      background-color: $cAddLight;
    }

    &--smallerDash {
      &:before {
        width: 1rem;
      }
    }
  }

  &__inputWrapper {
    position: relative;
    max-width: calc(50% - 3rem);

    &--wider {
      max-width: unset;
      flex: 0 0 50%;

      &:first-of-type {
        padding-right: 1rem;

        @include rwd('small-tablet') {
          padding-right: 0.5rem;
        }
      }

      &:last-of-type {
        padding-left: 1rem;

        @include rwd('small-tablet') {
          padding-left: 0.5rem;
        }
      }
    }
  }

  &__input {
    width: 100%;
    padding: 1rem;
    height: 4rem;
    border: 1px solid $cAddLight;
    border-radius: 5px;
    box-shadow: none;
    font-size: 1.4rem;
    line-height: 2rem;
    color: $cMainLight;

    &--tall {
      height: 4.8rem;
    }

    &::placeholder {
      color: $cAddDark02;
    }
  }

  &__postfix {
    position: absolute;
    right: 2rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.4rem;
    line-height: 2rem;
    color: $cMainLight;
  }

  &__label {
    display: block;
    position: relative;
  }

  &__labelText {
    white-space: nowrap;
    overflow: hidden;
    max-width: calc(100% - 2rem);
    font-size: 1.4rem;
    line-height: 1.8rem;
    font-weight: 600;
    color: $cAddDark02;
    background-color: $cWhite;
    margin-left: 1rem;
    padding: 0 1rem;
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    left: 0;
    z-index: 10;
    transition: top $trM;
    pointer-events: none;

    @include rwd('tablet') {
      padding: 0 0.5rem;
    }

    &--top {
      top: 0;
      color: $cMainDark;
    }
  }

  &__range {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
